import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Badge } from '@components/ui/badge';
import { MultiCombobox } from '../combobox';
import { CommunityPageEntrySupportDto } from '@api/ChallengeClient';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';

const PAGE_SIZE = 30;

export interface CommunityMultiSelectProps {
    value: Pick<CommunityPageEntrySupportDto, 'id' | 'name'>[];
    onChange: (value: CommunityPageEntrySupportDto[]) => void;
    disabled?: boolean;
    warningIds?: number[];
    successIds?: number[];
}

export const CommunityMultiSelect: React.FC<CommunityMultiSelectProps> = ({
    value,
    onChange,
    disabled,
    warningIds,
    successIds
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [options, setOptions] = useState<CommunityPageEntrySupportDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const { challengeClient } = injectTSDI(Api);

    const fetchPage = useCallback(
        async (nextPage: number, search: string) => {
            const response =
                await challengeClient.support.communitySupportControllerGetPage(
                    {
                        search: search || undefined,
                        page: nextPage,
                        size: PAGE_SIZE
                    }
                );
            const data = response.data;
            setHasMore(data.number < data.totalPages - 1);
            return data.content;
        },
        [challengeClient]
    );

    useEffect(() => {
        let active = true;
        const timerId = setTimeout(async () => {
            setLoading(true);
            try {
                const content = await fetchPage(0, searchTerm);
                if (active) {
                    setOptions(content);
                    setPage(0);
                }
            } catch (error) {
                console.error('Error fetching communities:', error);
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        }, 300);
        return () => {
            active = false;
            clearTimeout(timerId);
        };
    }, [searchTerm, fetchPage]);

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) {
            return;
        }
        setLoading(true);
        try {
            const nextPage = page + 1;
            const content = await fetchPage(nextPage, searchTerm);
            setOptions((prev) => {
                const seen = new Set(prev.map((community) => community.id));
                return [
                    ...prev,
                    ...content.filter((community) => !seen.has(community.id))
                ];
            });
            setPage(nextPage);
        } catch (error) {
            console.error('Error fetching communities:', error);
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, page, searchTerm, fetchPage]);

    const chipVariant = (id: number) =>
        warningIds?.includes(id)
            ? 'warning'
            : successIds?.includes(id)
              ? 'success'
              : 'secondary';

    return (
        <MultiCombobox
            label="Select Communities"
            placeholder="Search communities..."
            searchPlaceholder="Search communities..."
            options={options}
            getOptionLabel={(option) => option.name || 'Unnamed Community'}
            getOptionKey={(option) => option.id}
            value={value as CommunityPageEntrySupportDto[]}
            disabled={disabled}
            onChange={onChange}
            onInputChange={setSearchTerm}
            onScrollEnd={() => void loadMore()}
            loading={loading}
            renderChip={(option, remove) => (
                <Badge variant={chipVariant(option.id)} className="gap-1">
                    {option.name || 'Unnamed Community'}
                    {!disabled && (
                        <X
                            className="size-3 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                remove();
                            }}
                        />
                    )}
                </Badge>
            )}
            renderOption={(option) => (
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">
                        {option.name || `Community #${option.id}`}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                        #{option.id}
                    </p>
                </div>
            )}
        />
    );
};
